import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AlertCircle, ArrowLeftRight, CalendarDays, Coins, Eraser, HelpCircle, Info, LogOut, RefreshCw, ShoppingCart, Sprout } from 'lucide-react';
import { apiService, type StorePointFlowsResponse } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { formatPlayerName } from '@/utils/playerName';
import { BarList, ColumnChart, type ColumnDatum } from './JobsCharts';
import { dayLabel, formatInstant, shortDay } from './jobStats';
import { shortUuid } from './moneyLeaderboard';
import { formatPoints } from './shopStats';
import {
  DESTINATION_ORDER, FLOWS_SATELLITE_VERSION, POINTS_TYPES, SOURCE_ORDER, clearLabel, destinationHint, destinationLabel, flowColor,
  percentOf, reachesBeforeDebitFix, sourceHint, sourceLabel, typeWord, type PointsType,
} from './storePointFlows';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';
import sh from './ShopSection.module.scss';
import fl from './StorePointFlowsBlock.module.scss';

/** Même choix de période que les sections Boutique et Box. */
const PERIODS = [7, 14, 30, 90] as const;

/** Lignes de la liste « clear » avant « Voir les 100 dernières » (le satellite en rend 100). */
const CLEAR_ROWS = 20;

/**
 * Mouvements des points boutique, sous les soldes de la section Points Boutique : points créés par source, dépensés
 * par destination (boutique, market, échanges...), la part passée à d'autres joueurs et celle sortie du jeu, et les
 * retraits qui ne sont pas une dépense (« clear ») avec les soldes remis. Saisonniers par défaut. Lu par le satellite
 * 1.0.34 dans l'historique de StorePointApi ; ce que l'historique ne note pas est dit, pas deviné.
 */
export function StorePointFlowsBlock() {
  const gameId = useGameStore((s) => s.activeGameId);
  const [days, setDays] = useState<number>(30);
  const [type, setType] = useState<PointsType>('seasonal');
  const [data, setData] = useState<StorePointFlowsResponse | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [allClears, setAllClears] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getStorePointFlows(days, type);
    if (res.data) { setData(res.data); setError(null); }
    else {
      setData(null);
      setError({
        status: res.status,
        message: res.status === 404
          ? `Le satellite de ce projet ne sert pas encore les mouvements des points boutique : il faut le mettre à jour (satellite ${FLOWS_SATELLITE_VERSION} ou plus, à redéployer dans Déploiement > APIs).`
          : res.status === 403 ? "Ton grade n'a pas accès à cette section des statistiques."
            : res.error?.trim() || "Les mouvements des points boutique n'ont pas pu être lus.",
      });
    }
    setLoading(false);
  }, [days, type, gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  const count = (n: number) => n.toLocaleString('fr-FR');
  const plural = (n: number, word: string) => `${count(n)} ${word}${Math.abs(n) > 1 ? 's' : ''}`;
  const fmt = (n: number) => formatPoints(n);
  const word = typeWord(type);

  const head = (
    <div className={styles.blockHead}>
      <h3><ArrowLeftRight size={18} /> Mouvements des points boutique</h3>
      <div className={styles.controls}>
        <select className={lb.select} value={type} onChange={(e) => setType(e.target.value as PointsType)} aria-label="Type de points">
          {POINTS_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select className={lb.select} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Période">
          {PERIODS.map((p) => <option key={p} value={p}>{p} jours</option>)}
        </select>
      </div>
    </div>
  );

  let body: ReactNode = null;
  if (loading && !data && !error) {
    body = <div className={lb.state}><div className={lb.spinner} /><p>Lecture de l'historique des points...</p></div>;
  } else if (error && !data) {
    body = (
      <div className={lb.state}>
        {error.status === 404 ? <Info size={28} className={styles.stateIcon} /> : <AlertCircle size={28} />}
        <h3>{error.status === 404 ? 'Satellite à mettre à jour' : 'Mouvements illisibles'}</h3>
        <p className={styles.stateText}>{error.message}</p>
        <button className={lb.buttonGhost} onClick={() => void load()}><RefreshCw size={16} /> Vérifier de nouveau</button>
      </div>
    );
  } else if (data && !data.available) {
    body = <div className={lb.state}><Coins size={28} /><h3>Pas d'historique des points ici</h3><p>{data.reason}</p></div>;
  }

  if (body || !data) {
    return (
      <div className={styles.section}>
        <section className={styles.block}>{head}{body}</section>
      </div>
    );
  }

  const t = data.totals!;
  const balance = t.created - t.destroyed;
  const sources = data.sources.filter((s) => s.amount > 0).sort((a, b) => b.amount - a.amount);
  const destinations = data.destinations.filter((d) => d.spent > 0 || d.refunded > 0 || d.toPlayers > 0);
  const presentSources = SOURCE_ORDER.filter((id) => data.daily.some((d) => (d.bySource[id] ?? 0) > 0));
  const presentDestinations = DESTINATION_ORDER.filter((id) => data.daily.some((d) => (d.byDestination[id] ?? 0) > 0));
  const clears = allClears ? data.clears : data.clears.slice(0, CLEAR_ROWS);

  const cards = [
    { icon: Sprout, value: fmt(t.created), label: `Points${word} créés`, sub: `${plural(t.createdPlayers, 'joueur')} crédité${t.createdPlayers > 1 ? 's' : ''}` },
    {
      icon: ShoppingCart, value: fmt(t.consumed), label: `Points${word} dépensés`,
      sub: t.toPlayers > 0 ? `dont ${fmt(t.toPlayers)} passés à d'autres joueurs` : 'remboursements déduits',
    },
    { icon: LogOut, value: fmt(t.destroyed), label: 'Sortis du jeu', sub: 'boutique, taxes, emplacements, retraits' },
    {
      icon: Eraser, value: fmt(t.cleared), label: 'Clear (retraits hors dépense)',
      sub: t.sets > 0 ? `et ${plural(t.sets, 'solde')} remis à une valeur` : 'aucun solde remis à une valeur',
    },
  ];

  const createdCols: ColumnDatum[] = data.daily.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: presentSources.map((id) => ({ id, label: sourceLabel(id), value: d.bySource[id] ?? 0, color: flowColor(id) })),
  }));
  const consumedCols: ColumnDatum[] = data.daily.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: presentDestinations.map((id) => ({ id, label: destinationLabel(id), value: Math.max(0, d.byDestination[id] ?? 0), color: flowColor(id) })),
  }));

  return (
    <div className={styles.section}>
      <section className={styles.block}>
        {head}
        <div className={lb.cards}>
          {cards.map((c) => (
            <div key={c.label} className={lb.card}>
              <div className={lb.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
              <div className={lb.cardText}>
                <span className={lb.cardValue}>{c.value}</span>
                <span className={lb.cardLabel}>{c.label}</span>
                <span className={lb.cardSub}>{c.sub}</span>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.info}><Info size={16} />
          <span>
            Mouvements des points{word} des {data.days} derniers jours, d'après l'historique des points boutique
            {data.since ? ` (tenu depuis le ${formatInstant(data.since).split(' à ')[0]})` : ''}. Les points dépensés au market
            et en échange passent en partie à d'autres joueurs : seuls la taxe et ce que personne ne reçoit sortent du jeu.
            Bilan de la période : {balance >= 0 ? '+' : '-'}{fmt(Math.abs(balance))} dans le jeu (créés moins sortis du jeu).
          </span>
        </div>
        {reachesBeforeDebitFix(data.days) && (
          <div className={lb.notice}><AlertCircle size={16} />
            <span>
              Jusqu'à StorePointApi 1.0.3 (27/10/2026), un retrait que le solde ne couvrait pas était noté en entier alors que
              le solde tombait seulement à 0 : avant cette date, des achats au market et des échanges sont notés plus chers que
              ce qui a vraiment été pris.
            </span>
          </div>
        )}
      </section>

      <section className={styles.block}>
          <div className={styles.blockHead}><h3><Sprout size={18} /> D'où viennent les points{word}</h3></div>
          {sources.length === 0 ? <p className={lb.empty}>Aucun point créé sur la période.</p> : (
            <BarList formatValue={fmt} rows={sources.map((s) => ({
              id: s.id, label: sourceLabel(s.id), color: flowColor(s.id), value: s.amount, share: s.amount,
              sub: `${plural(s.count, 'crédit')}, ${plural(s.players, 'joueur')}, ${percentOf(s.amount, t.created)}`,
            }))} />
          )}
          {sources.some((s) => sourceHint(s.id)) && (
            <ul className={fl.notes}>
              {sources.filter((s) => sourceHint(s.id)).map((s) => <li key={s.id}><b>{sourceLabel(s.id)}</b> : {sourceHint(s.id)}</li>)}
            </ul>
          )}
      </section>

      <section className={styles.block}>
          <div className={styles.blockHead}><h3><ShoppingCart size={18} /> Dans quoi ils sont dépensés</h3></div>
          {destinations.length === 0 ? <p className={lb.empty}>Aucun point dépensé sur la période.</p> : (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead>
                  <tr>
                    <th>Destination</th><th className={lb.num} title="Dépensés moins remboursés">Dépensés</th>
                    <th className={lb.num}>Remboursés</th><th className={lb.num} title="Reçus par un autre joueur (vente, échange)">À d'autres joueurs</th>
                    <th className={lb.num}>Sortis du jeu</th><th className={lb.num}>Part</th>
                  </tr>
                </thead>
                <tbody>
                  {destinations.map((d) => (
                    <tr key={d.id} title={destinationHint(d.id)}>
                      <td>
                        <span className={sh.product}>
                          <span className={sh.categoryCell}><span className={styles.swatch} style={{ background: flowColor(d.id) }} />{destinationLabel(d.id)}</span>
                          <span className={sh.productSub}>{plural(d.count, 'opération')}, {plural(d.players, 'joueur')}</span>
                        </span>
                      </td>
                      <td className={lb.num}>{fmt(d.net)}</td>
                      <td className={lb.num}>{d.refunded > 0 ? fmt(d.refunded) : '-'}</td>
                      <td className={lb.num}>{d.toPlayers > 0 ? fmt(d.toPlayers) : '-'}</td>
                      <td className={lb.num}>{fmt(d.destroyed)}</td>
                      <td className={lb.num}>{percentOf(d.net, t.consumed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {destinations.some((d) => destinationHint(d.id)) && (
            <ul className={fl.notes}>
              {destinations.filter((d) => destinationHint(d.id)).map((d) => <li key={d.id}><b>{destinationLabel(d.id)}</b> : {destinationHint(d.id)}</li>)}
            </ul>
          )}
      </section>

      <section className={styles.block}>
        <div className={styles.blockHead}><h3><CalendarDays size={18} /> Par jour</h3></div>
        <div className={styles.grid2}>
          <div>
            <h4 className={styles.blockSub}>Points{word} créés</h4>
            <ColumnChart data={createdCols} formatValue={fmt} formatTick={count} ariaLabel={`Points${word} créés par jour`} height={190}
              legend={presentSources.map((id) => ({ id, label: sourceLabel(id), color: flowColor(id) }))} />
          </div>
          <div>
            <h4 className={styles.blockSub}>Points{word} dépensés</h4>
            <ColumnChart data={consumedCols} formatValue={fmt} formatTick={count} ariaLabel={`Points${word} dépensés par jour`} height={190}
              legend={presentDestinations.map((id) => ({ id, label: destinationLabel(id), color: flowColor(id) }))} />
          </div>
        </div>
      </section>

      <section className={styles.block}>
        <div className={styles.blockHead}><h3><Eraser size={18} /> Clear : retraits hors dépense et soldes remis</h3></div>
        {data.clears.length === 0 ? <p className={lb.empty}>Aucun retrait hors dépense ni solde remis sur la période.</p> : (
          <>
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead><tr><th>Quand</th><th>Joueur</th><th>Opération</th><th className={lb.num}>Points</th><th>Type</th><th>Motif noté</th></tr></thead>
                <tbody>
                  {clears.map((c) => (
                    <tr key={`${c.at}-${c.uuid}-${c.category}-${c.amount}`}>
                      <td className={sh.when}>{formatInstant(c.at)}</td>
                      <td><span className={lb.playerName}>{formatPlayerName(c.name, c.displayName, shortUuid(c.uuid))}</span></td>
                      <td>{clearLabel(c.category)}</td>
                      <td className={lb.num}>{c.category === 'set' ? `remis à ${fmt(c.amount)}` : `-${fmt(c.amount)}`}</td>
                      <td>{c.seasonal ? 'Saisonniers' : 'Classiques'}</td>
                      <td className={sh.productSub}>{c.reason || '(aucun)'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.clears.length > CLEAR_ROWS && (
              <button className={lb.buttonGhost} onClick={() => setAllClears((v) => !v)}>
                {allClears ? 'Voir moins' : `Voir les ${count(data.clears.length)} dernières opérations`}
              </button>
            )}
          </>
        )}
        <p className={styles.muted}>
          Retraits qui ne sont ni un achat (boutique, market, emplacement) ni un échange : retrait sans motif, ou motif que le
          panel ne connaît pas. Un solde remis à une valeur ne note que le nouveau solde, pas ce qui a été retiré. Qui a fait
          l'opération n'est pas noté (StorePointApi ne garde pas l'auteur), et une remise à zéro faite directement dans la base
          n'apparaît pas ici.
        </p>
      </section>

      {data.unknown.length > 0 && (
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><HelpCircle size={18} /> Motifs pas encore classés</h3></div>
          <div className={lb.tableWrap}>
            <table className={lb.table}>
              <thead><tr><th>Motif (début)</th><th>Sens</th><th className={lb.num}>Points</th><th className={lb.num}>Opérations</th></tr></thead>
              <tbody>
                {data.unknown.map((u) => (
                  <tr key={`${u.direction}|${u.reason}`}>
                    <td>{u.reason}</td>
                    <td>{u.direction === 'in' ? 'Crédit' : 'Retrait'}</td>
                    <td className={lb.num}>{fmt(u.amount)}</td>
                    <td className={lb.num}>{count(u.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={styles.muted}>Comptés dans « Autres crédits » ou « Autres retraits » : un motif nouveau d'un mod peut recevoir sa catégorie.</p>
        </section>
      )}

      {data.namesPartial && (
        <div className={lb.notice}><AlertCircle size={16} /><span>Certains joueurs n'ont pas encore de pseudo connu : leur uuid est affiché à la place.</span></div>
      )}
    </div>
  );
}
